import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { Label } from '@cortex/web-design-system/components/ui/Label';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import {
  isUrgentUrgency,
  OPS_SELECTABLE_CATEGORIES,
  OPS_SERVICE_LABELS,
  OPS_SERVICE_SLUGS,
  opsDisplayNo,
  parseUserIds,
  validateOpsRequestDraft,
  wfidRequiredFor,
  type OpsFieldError,
  type OpsRequestDraft,
  type OpsRequestDraftErrors,
} from '@cortex/ops-core';
import { AccentBox } from '../components/features/ops/OpsPanels.js';
import { opsCategoryLabel } from '../components/features/ops/OpsPrimitives.js';
import {
  RelatedScanResults,
  scanLinkKey,
  SimilarScanResults,
} from '../components/features/ops/ScanResults.js';
import type { OpsRelatedTicketInput } from '../lib/api/ops.js';
import { OpsSubmitAttachments } from '../components/features/ops/OpsSubmitAttachments.js';
import {
  OpsFollowUpBanner,
  OpsFollowUpOriginPending,
  useOpsFollowUpOrigin,
  type OpsFollowUpOrigin,
} from '../components/features/ops/OpsFollowUpSubmit.js';
import { useAuth } from '../hooks/useAuth.js';
import { useCreateOpsRequest, useOpsRelatedRequests, useOpsSimilar } from '../hooks/useOps.js';
import { useOpsDraftAutosave, useRestoredOpsDraft } from '../hooks/useOpsSubmitDraft.js';
import type { OpsCategory, OpsService, OpsUrgency } from '../types/ops.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 新規依頼フォーム（依頼者ビュー）。guided な必須入力で irai 品質を担保する: User ID は数字 or N/A を強制、自由記述は品質チェック。状態変更 / データ修正では WFID を警告色でプロアクティブに促すが非ブロック。緊急（即時 / 当日）を選ぶと「影響が大きい依頼のみ」の使いどころを警告色で常時ガイドする（非ブロック）。検証は @cortex/ops-core の validateOpsRequestDraft を単一 source とし、フォームが導き API が gate する。送信成功後はフォームを畳んで確認状態（採番リンク + 「別の依頼を作成」）へ切り替え、送信ボタン自体を外すことで誤操作による二重送信を防ぐ。?followUpOf=<id> で開くと補足対応の起票になり、完了済みの元依頼からサービス・対象ユーザー・背景・WFID・CS チケット URL を引き継ぎ、種別は未選択（選び直し必須）・対応依頼は空で始まる。補足対応では下書きの復元・保存をしない（通常の下書きを上書きしない）。demo.html の New Request を PIL で再現
 * @graph-connects useAuth [calls] 起票者（サインイン中ユーザー）の識別
 * @graph-connects useCreateOpsRequest [calls] 新規依頼の作成（POST /ops/requests）を実行
 * @graph-connects OpsSubmitAttachments [references] 添付ファイルのステージング + 起票後アップロードを分離（submitted+opsId を渡して起票後にアップロードさせる）
 * @graph-connects useOpsFollowUpOrigin [calls] 補足対応の元依頼の取得・検証と引き継ぐ値
 * @graph-connects useRestoredOpsDraft [calls] 下書きの復元（補足対応では読まない）
 * @graph-connects useOpsDraftAutosave [calls] 下書きの自動保存・手動保存・破棄
 * @graph-connects OpsFollowUpBanner [references] 補足対応の元依頼バナー
 * @graph-connects OpsFollowUpOriginPending [references] 元依頼の取得中・取得失敗・起票できない表示
 */
export const Route = createFileRoute('/ops/submit')({
  // 補足対応の元依頼 id。空・非文字列は通常の起票として扱う。
  validateSearch: (search: Record<string, unknown>): { followUpOf?: string } =>
    typeof search['followUpOf'] === 'string' && search['followUpOf'] !== ''
      ? { followUpOf: search['followUpOf'] }
      : {},
  component: OpsSubmitPage,
});

/** サービス選択肢（slug / ラベルは ops-core を単一 source とし、サービス追加に自動追従する）。 @graph-connects none */
const SERVICE_OPTIONS: SelectOption[] = OPS_SERVICE_SLUGS.map((value) => ({
  value,
  label: OPS_SERVICE_LABELS[value],
}));

/** ユーザーが選べる ops カテゴリ（順序 = 表示順、default は先頭）。 @graph-connects none */
const SELECTABLE_CATEGORIES: readonly OpsCategory[] = OPS_SELECTABLE_CATEGORIES;

/** 緊急度選択肢の値と翻訳キー（label は t() で解決する）。 @graph-connects none */
const URGENCY_OPTIONS: { value: OpsUrgency; labelKey: string }[] = [
  { value: '通常', labelKey: 'ops.common.urgency.standard' },
  { value: '当日', labelKey: 'ops.common.urgency.sameDay' },
  { value: '即時', labelKey: 'ops.common.urgency.immediate' },
];

/**
 * ops-core の検証エラーコードを現在ロケールの文言に解決する（ページ本体と同じ t で i18n する）。
 * エラーが無ければ undefined を返し、フィールドはエラー未表示になる。
 * @graph-connects none
 */
function opsErrorText(error: OpsFieldError | undefined, t: TFunction): string | undefined {
  return error === undefined ? undefined : t(`ops.submit.errors.${error.code}`, error.params);
}

/** 保存時刻（epoch ms）を「Xm ago」等の相対表記にする（翻訳は t で解決）。 @graph-connects none */
function formatAgo(savedAt: number, t: TFunction): string {
  const mins = Math.floor((Date.now() - savedAt) / 60000);
  if (mins < 1) return t('ops.submit.ago.justNow');
  if (mins < 60) return t('ops.submit.ago.minutes', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('ops.submit.ago.hours', { count: hrs });
  return t('ops.submit.ago.days', { count: Math.floor(hrs / 24) });
}

/**
 * 補足対応なら元依頼を取得してからフォームを出す。key を元依頼で変え、通常の起票 ⇄ 補足対応の遷移で
 * フォームの状態を作り直す。
 * @graph-connects useOpsFollowUpOrigin [calls] 補足対応の元依頼の取得・検証
 */
function OpsSubmitPage(): ReactNode {
  const { followUpOf } = Route.useSearch();
  const origin = useOpsFollowUpOrigin(followUpOf);
  if (origin.status === 'none') return <OpsSubmitForm key="new" followUp={null} />;
  if (origin.status !== 'ready') return <OpsFollowUpOriginPending state={origin} />;
  return <OpsSubmitForm key={origin.origin.id} followUp={origin} />;
}

/** @graph-connects none */
function OpsSubmitForm({ followUp }: { followUp: OpsFollowUpOrigin | null }): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const seed = followUp?.seed;
  const { user } = useAuth();
  // 補足対応は種別を選び直すため未選択（null）で始め、選ぶまで送信させない。
  const categoryOptions: SelectOption[] = SELECTABLE_CATEGORIES.map((value) => ({
    value,
    label: opsCategoryLabel(value, t),
  }));
  const urgencyOptions: SelectOption[] = URGENCY_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));
  // 補足対応は下書きを復元しない（通常の下書きを元依頼の内容で上書きしないため、保存もしない）。
  const restored = useRestoredOpsDraft(followUp === null);
  const [service, setService] = useState<OpsService>(
    seed?.service ?? restored?.service ?? 'aircloset',
  );
  const [category, setCategory] = useState<OpsCategory | null>(
    followUp === null ? (restored?.category ?? '調査依頼') : null,
  );
  const [userId, setUserId] = useState(seed?.userIds?.join(', ') ?? restored?.userId ?? '');
  const [userNa, setUserNa] = useState(
    seed === undefined ? (restored?.userNa ?? false) : seed.userIds === null,
  );
  const [urgency, setUrgency] = useState<OpsUrgency>(restored?.urgency ?? '通常');
  const [background, setBackground] = useState(seed?.background ?? restored?.background ?? '');
  const [requested, setRequested] = useState(restored?.requested ?? '');
  const [wfid, setWfid] = useState(seed?.wfid ?? restored?.wfid ?? '');
  const [csUrl, setCsUrl] = useState(seed?.csTicketUrl ?? restored?.csUrl ?? '');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const createRequest = useCreateOpsRequest();
  const submitted = createdId !== null;
  const [scanTriggered, setScanTriggered] = useState(false);
  // 起票時に事前スキャン結果から選んで紐付ける初期関連チケット（任意・非ブロック）。
  const [stagedLinks, setStagedLinks] = useState<OpsRelatedTicketInput[]>([]);

  const wfidRequired = category !== null && wfidRequiredFor(category);
  // 状態変更 / データ修正 は WFID が要るのに未入力 → フィールドを警告色で強調し記入を促す。
  const wfidNeeded = wfidRequired && wfid.trim() === '';
  // 緊急（即時 / 当日）を選ぶと、緊急の使いどころ（大きな影響のみ）を警告色で常時ガイドする（非ブロック）。
  // 「緊急扱い」の判定は ops-core の isUrgentUrgency を単一 source として SLA 側と共有する。
  const urgentSelected = isUrgentUrgency(urgency);
  // 内容（背景 + 対応依頼）で類似 ops / 既存バグチケットを、User ID で同一ユーザーの既存 ops を洗い出す。
  const scanText = `${background}\n${requested}`.trim();
  // User ID はカンマ区切りで複数入力できる。分割規則は ops-core の parseUserIds に集約（N/A では検証・スキャンとも無効）。
  const parsedUserIds = parseUserIds(userId);
  const hasUser = !userNa && parsedUserIds.length > 0;
  const canScan = scanText !== '' || hasUser;
  const related = useOpsRelatedRequests(userNa ? [] : parsedUserIds, scanTriggered && hasUser);
  const similar = useOpsSimilar(scanText, scanTriggered && scanText !== '');
  const scanning = related.isFetching || similar.isFetching;

  // フォーム = guided、API = gate。検証ルールは @cortex/ops-core を単一 source として共有する。
  // 種別の未選択は ops-core の検証の外（API は enum で弾く）なので、フォームで別に止める。
  const categoryMissing = category === null;
  const draft: OpsRequestDraft = {
    service,
    category: category ?? SELECTABLE_CATEGORIES[0],
    urgency,
    // N/A は null（検証スキップ）。非 N/A は配列（空なら未入力で userIdsRequired）。
    userIds: userNa ? null : parsedUserIds,
    background,
    requested,
    wfid: wfid.trim() === '' ? null : wfid.trim(),
    csTicketUrl: csUrl.trim() === '' ? null : csUrl.trim(),
  };
  const liveErrors = validateOpsRequestDraft(draft);
  // 送信を試みるまではエラーを出さない。試行後は入力を直すたびに再評価されて消える（guided）。
  const errors: OpsRequestDraftErrors = submitAttempted ? liveErrors : {};

  // 入力があるあいだ下書きへ自動保存する。送信後・補足対応（・種別未選択）は保存しない。
  const draftStore = useOpsDraftAutosave(
    submitted || followUp !== null || category === null
      ? null
      : { service, category, userId, userNa, urgency, background, requested, wfid, csUrl },
    restored?.savedAt ?? null,
  );

  // フォーム入力を初期値へ戻す（下書き破棄・「別の依頼を作成」で共有）。
  const resetFields = (): void => {
    setService('aircloset');
    setCategory('調査依頼');
    setUserId('');
    setUserNa(false);
    setUrgency('通常');
    setBackground('');
    setRequested('');
    setWfid('');
    setCsUrl('');
    setSubmitAttempted(false);
    setStagedLinks([]);
  };

  // 事前スキャン結果 1 行の紐付けをトグルする（(target, ref) で一意）。起票時 linkedTickets に staging する。
  const toggleStagedLink = (input: OpsRelatedTicketInput): void => {
    setStagedLinks((prev) => {
      const exists = prev.some((l) => l.target === input.target && l.ref === input.ref);
      return exists
        ? prev.filter((l) => !(l.target === input.target && l.ref === input.ref))
        : [...prev, input];
    });
  };

  const handleDiscardDraft = (): void => {
    draftStore.discard();
    resetFields();
  };

  // 送信成功後の確認状態から、空フォームで次の依頼を作成する（補足対応からは通常の起票フォームへ移る）。
  const handleFileAnother = (): void => {
    if (followUp !== null) {
      void navigate({ to: '/ops/submit', search: {} });
      return;
    }
    resetFields();
    setScanTriggered(false);
    setCreatedId(null);
  };

  // 事前スキャン: 対象ユーザーの既存 ops を突き合わせる（重い AI 調査は送信後）。フックが実データを取得する。
  const runScan = (): void => {
    setScanTriggered(true);
  };

  // 二重送信ガード: 送信中（isPending）・送信済み（submitted）は再送しない。送信後は UI からボタン自体を消すが、
  // Enter キー等の経路も含めてここでも確実に弾く（サーバー冪等性は未実装のため client 側で防ぐ）。
  const busy = createRequest.isPending;

  const handleSubmit = (): void => {
    if (busy || submitted) return;
    setSubmitAttempted(true);
    if (categoryMissing || Object.keys(liveErrors).length > 0) return;
    // 起票時の事前スキャン結果（実行済みなら）と staging した関連チケットを一緒に焼き付ける（任意・非ブロック）。
    createRequest.mutate(
      {
        draft,
        extras: {
          preScan: similar.data,
          linkedTickets: stagedLinks.length > 0 ? stagedLinks : undefined,
          followUpOf: followUp?.origin.id,
        },
      },
      {
        onSuccess: (created) => {
          // 補足対応は下書きを使っていないので、別に書きかけの通常の下書きを消さない。
          if (followUp === null) draftStore.discard();
          // 確認状態へ切替。ステージ済み添付は OpsSubmitAttachments が submitted+opsId を見て起票後にアップロードする。
          setCreatedId(created.id);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader title={t('ops.submit.cardTitle')} description={t('ops.submit.cardDescription')} />
      <CardContent className="space-y-4">
        {/* 送信成功後は確認状態に切替える: フォームと送信ボタンを外し、二重送信を構造的に不可能にする。
            採番された依頼へのリンクと「別の依頼を作成」（フォームをリセットして再表示）だけを出す。 */}
        {submitted && createdId !== null && (
          <div className="space-y-4">
            <AccentBox tone="success" title={t('ops.submit.submittedTitle')}>
              {t('ops.submit.submittedBody')}{' '}
              <Link
                to="/ops/requests/$id"
                params={{ id: createdId }}
                className="font-semibold text-[var(--color-accent-primary)] hover:underline"
              >
                #{opsDisplayNo(createdId)}
              </Link>{' '}
              {t('ops.submit.submittedSaved')}
              {t('ops.submit.submittedFollowup')}
            </AccentBox>
            <Button type="button" variant="primary" onClick={handleFileAnother}>
              {t('ops.submit.fileAnother')}
            </Button>
          </div>
        )}

        {!submitted && (
          <>
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('ops.submit.filingAs')}{' '}
              <span className="font-semibold text-[var(--color-text-primary)]">
                {user?.nickName ?? '—'}
              </span>
            </p>

            {followUp !== null && <OpsFollowUpBanner origin={followUp.origin} />}

            {draftStore.notice !== null && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-hover)] px-3 py-2 text-xs">
                <span className="text-[var(--color-text-secondary)]">
                  {t('ops.submit.draftRestored', { ago: formatAgo(draftStore.notice, t) })}
                </span>
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="shrink-0 font-semibold text-[var(--color-accent-primary)] hover:underline"
                >
                  {t('ops.submit.discard')}
                </button>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label={t('ops.submit.fields.service')}
                options={SERVICE_OPTIONS}
                value={service}
                onChange={(e) => {
                  setService(e.target.value as OpsService);
                }}
                fullWidth
              />
              <Select
                label={t('ops.submit.fields.category')}
                options={categoryOptions}
                value={category ?? ''}
                placeholder={
                  categoryMissing ? t('ops.submit.followUp.categoryPlaceholder') : undefined
                }
                error={
                  submitAttempted && categoryMissing
                    ? t('ops.submit.followUp.categoryRequired')
                    : undefined
                }
                onChange={(e) => {
                  setCategory(e.target.value as OpsCategory);
                }}
                fullWidth
              />
            </div>

            <div className="grid items-start gap-3 sm:grid-cols-2">
              <div>
                <Input
                  label={`${t('ops.submit.fields.userId')}${userNa ? '' : t('ops.submit.fields.userIdRequiredMark')}`}
                  value={userNa ? '' : userId}
                  onChange={(e) => {
                    setUserId(e.target.value);
                  }}
                  disabled={userNa}
                  error={opsErrorText(errors.userIds, t)}
                  placeholder={
                    userNa
                      ? t('ops.submit.fields.userNaPlaceholder')
                      : t('ops.submit.fields.userIdPlaceholder')
                  }
                  fullWidth
                />
                <label
                  htmlFor="ops-user-na"
                  className="mt-1.5 flex items-center gap-2 text-sm text-[var(--color-text-secondary)]"
                >
                  <Checkbox
                    id="ops-user-na"
                    checked={userNa}
                    onChange={(e) => {
                      setUserNa(e.target.checked);
                    }}
                  />
                  {t('ops.submit.fields.userNaLabel')}
                </label>
              </div>
              <div>
                <Select
                  label={t('ops.submit.fields.urgency')}
                  options={urgencyOptions}
                  value={urgency}
                  onChange={(e) => {
                    setUrgency(e.target.value as OpsUrgency);
                  }}
                  aria-describedby="ops-urgency-guide"
                  fullWidth
                />
                <p
                  id="ops-urgency-guide"
                  className={`mt-1.5 text-xs ${
                    urgentSelected
                      ? 'font-semibold text-[var(--color-warning)]'
                      : 'text-[var(--color-text-muted)]'
                  }`}
                >
                  {t('ops.submit.fields.urgencyGuide')}
                </p>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="ops-background">{t('ops.submit.fields.background')}</Label>
              <Textarea
                id="ops-background"
                rows={3}
                value={background}
                onChange={(e) => {
                  setBackground(e.target.value);
                }}
              />
              {errors.background !== undefined && (
                <p className="text-xs text-[var(--color-error)]">
                  {opsErrorText(errors.background, t)}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ops-requested">{t('ops.submit.fields.requested')}</Label>
              <Textarea
                id="ops-requested"
                rows={2}
                value={requested}
                onChange={(e) => {
                  setRequested(e.target.value);
                }}
              />
              {errors.requested !== undefined && (
                <p className="text-xs text-[var(--color-error)]">
                  {opsErrorText(errors.requested, t)}
                </p>
              )}
            </div>

            <div className="grid items-start gap-3 sm:grid-cols-2">
              <div
                className={
                  wfidNeeded
                    ? 'rounded-md border border-[var(--color-warning)] bg-[var(--color-warning-muted)] p-2.5'
                    : undefined
                }
              >
                {wfidNeeded && (
                  <p className="mb-1.5 text-xs font-semibold text-[var(--color-warning)]">
                    {t('ops.submit.fields.wfidNeeded', { category: opsCategoryLabel(category, t) })}
                  </p>
                )}
                <Input
                  label={t('ops.submit.fields.wfid')}
                  value={wfid}
                  onChange={(e) => {
                    setWfid(e.target.value);
                  }}
                  helperText={
                    wfidRequired
                      ? t('ops.submit.fields.wfidHelperRecommended')
                      : t('ops.submit.fields.wfidHelperOptional')
                  }
                  placeholder={t('ops.submit.fields.wfidPlaceholder')}
                  fullWidth
                />
              </div>
              <Input
                label={t('ops.submit.fields.csUrl')}
                value={csUrl}
                onChange={(e) => {
                  setCsUrl(e.target.value);
                }}
                error={opsErrorText(errors.csTicketUrl, t)}
                placeholder={t('ops.submit.fields.csUrlPlaceholder')}
                fullWidth
              />
            </div>

            {/* Pre-request scan — 内容の類似 ops / 既存バグチケット + 同一ユーザーの既存 ops で重複を洗い出す */}
            <div>
              <Button
                type="button"
                variant="secondary"
                loading={scanning}
                disabled={!canScan}
                onClick={runScan}
              >
                {t('ops.submit.scan.button')}
              </Button>
              {!canScan && (
                <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                  {t('ops.submit.scan.needInput')}
                </p>
              )}
              {scanTriggered && scanning && (
                <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                  {t('ops.submit.scan.scanning')}
                </p>
              )}
              {scanTriggered && !scanning && (related.isError || similar.isError) && (
                <p className="mt-2 text-xs text-[var(--color-error)]">
                  {t('ops.submit.scan.failed')}
                </p>
              )}
              {scanTriggered && !scanning && (
                <div className="mt-2">
                  <AccentBox title={t('ops.submit.scan.resultTitle')}>
                    <div className="space-y-3">
                      {similar.data !== undefined && (
                        <SimilarScanResults
                          result={similar.data}
                          staging={{
                            stagedKeys: new Set(
                              stagedLinks.map((l) => scanLinkKey(l.target, l.ref)),
                            ),
                            onToggle: toggleStagedLink,
                          }}
                        />
                      )}
                      {related.data !== undefined && related.data.items.length > 0 && (
                        <div>
                          <div className="mb-1 text-xs font-semibold text-[var(--color-text-muted)]">
                            {t('ops.submit.scan.sameUser')}
                          </div>
                          <RelatedScanResults
                            items={related.data.items}
                            total={related.data.total}
                          />
                        </div>
                      )}
                    </div>
                  </AccentBox>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button type="button" variant="primary" loading={busy} onClick={handleSubmit}>
                {t('ops.submit.submitButton')}
              </Button>
              {followUp === null && (
                <Button type="button" variant="ghost" disabled={busy} onClick={draftStore.save}>
                  {t('ops.submit.saveDraft')}
                </Button>
              )}
              {draftStore.savedFlash && (
                <span className="text-xs text-[var(--color-success)]">
                  {t('ops.submit.draftSaved')}
                </span>
              )}
            </div>
            {submitAttempted && (categoryMissing || Object.keys(errors).length > 0) && (
              <p className="text-xs text-[var(--color-error)]">{t('ops.submit.fixErrors')}</p>
            )}
            {createRequest.isError && (
              <p className="text-xs text-[var(--color-error)]">{t('ops.submit.submitFailed')}</p>
            )}
          </>
        )}

        {/* 添付ファイル（任意）— 常時マウントし、起票→確認をまたいで staging を保持しつつ起票後にアップロードする。 */}
        <OpsSubmitAttachments t={t} submitted={submitted} opsId={createdId} />
      </CardContent>
    </Card>
  );
}
